import { useMemo } from 'react';
import { Check, X } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

interface PasswordStrengthIndicatorProps {
  password: string;
}

interface Requirement {
  key: string;
  test: (password: string) => boolean;
  labelEn: string;
  labelEs: string;
}

const requirements: Requirement[] = [
  {
    key: 'length',
    test: (p) => p.length >= 8,
    labelEn: 'At least 8 characters',
    labelEs: 'Al menos 8 caracteres',
  },
  {
    key: 'uppercase',
    test: (p) => /[A-Z]/.test(p),
    labelEn: 'One uppercase letter',
    labelEs: 'Una letra mayúscula',
  },
  {
    key: 'lowercase',
    test: (p) => /[a-z]/.test(p),
    labelEn: 'One lowercase letter',
    labelEs: 'Una letra minúscula',
  },
  {
    key: 'number',
    test: (p) => /\d/.test(p),
    labelEn: 'One number',
    labelEs: 'Un número',
  },
  {
    key: 'special',
    test: (p) => /[!@#$%^&*(),.?":{}|<>]/.test(p),
    labelEn: 'One special character (!@#$%...)',
    labelEs: 'Un carácter especial (!@#$%...)',
  },
];

export function PasswordStrengthIndicator({ password }: PasswordStrengthIndicatorProps) {
  const { language } = useLanguage();

  const { passed, strength } = useMemo(() => {
    const passedReqs = requirements.filter((req) => req.test(password));
    const passedCount = passedReqs.length;
    
    let strength: 'weak' | 'medium' | 'strong' = 'weak';
    if (passedCount >= 4) strength = 'medium';
    if (passedCount === 5) strength = 'strong';
    
    return { passed: passedReqs.map((r) => r.key), strength };
  }, [password]);

  const strengthColors = {
    weak: 'bg-destructive',
    medium: 'bg-yellow-500',
    strong: 'bg-green-500',
  };

  const strengthLabels = {
    weak: { en: 'Weak', es: 'Débil' },
    medium: { en: 'Medium', es: 'Media' },
    strong: { en: 'Strong', es: 'Fuerte' },
  };

  if (!password) return null;

  return (
    <div className="space-y-3 mt-2">
      {/* Strength bar */}
      <div className="space-y-1">
        <div className="flex gap-1">
          {[1, 2, 3].map((level) => (
            <div
              key={level}
              className={`h-1.5 flex-1 rounded-full transition-colors ${
                (strength === 'weak' && level === 1) ||
                (strength === 'medium' && level <= 2) ||
                (strength === 'strong')
                  ? strengthColors[strength]
                  : 'bg-muted'
              }`}
            />
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          {language === 'en' ? 'Password strength: ' : 'Seguridad: '}
          <span className={`font-medium ${
            strength === 'weak' ? 'text-destructive' : 
            strength === 'medium' ? 'text-yellow-500' : 'text-green-500'
          }`}>
            {strengthLabels[strength][language]}
          </span>
        </p>
      </div>

      {/* Requirements list */}
      <ul className="space-y-1">
        {requirements.map((req) => {
          const isPassed = passed.includes(req.key);
          return (
            <li key={req.key} className="flex items-center gap-2 text-xs">
              {isPassed ? (
                <Check className="h-3 w-3 text-green-500" />
              ) : (
                <X className="h-3 w-3 text-muted-foreground" />
              )}
              <span className={isPassed ? 'text-muted-foreground' : 'text-muted-foreground/70'}>
                {language === 'en' ? req.labelEn : req.labelEs}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
